// src/pages/seasons/LineupBuilder.jsx
'use client';

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, orderBy,
  onSnapshot, setDoc, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import { Button } from '@/components/button';
import { Heading, Subheading } from '@/components/heading';
import { Text } from '@/components/text';

import {
  ArrowLeftIcon,
  ArrowPathIcon,
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';

/* ─────────────────────────────────────────────
   Formations
───────────────────────────────────────────── */
const FORMATIONS = {
  '4-3-3': [
    { id: 'gk',  label: 'GK', x: 50, y: 85 },
    { id: 'rb',  label: 'RB', x: 80, y: 65 },
    { id: 'cb1', label: 'CB', x: 62, y: 65 },
    { id: 'cb2', label: 'CB', x: 38, y: 65 },
    { id: 'lb',  label: 'LB', x: 20, y: 65 },
    { id: 'cm1', label: 'CM', x: 68, y: 44 },
    { id: 'cm2', label: 'CM', x: 50, y: 44 },
    { id: 'cm3', label: 'CM', x: 32, y: 44 },
    { id: 'rw',  label: 'RW', x: 78, y: 18 },
    { id: 'st',  label: 'ST', x: 50, y: 14 },
    { id: 'lw',  label: 'LW', x: 22, y: 18 },
  ],
  '4-4-2': [
    { id: 'gk',  label: 'GK', x: 50, y: 85 },
    { id: 'rb',  label: 'RB', x: 80, y: 65 },
    { id: 'cb1', label: 'CB', x: 62, y: 65 },
    { id: 'cb2', label: 'CB', x: 38, y: 65 },
    { id: 'lb',  label: 'LB', x: 20, y: 65 },
    { id: 'rm',  label: 'RM', x: 80, y: 44 },
    { id: 'cm1', label: 'CM', x: 60, y: 44 },
    { id: 'cm2', label: 'CM', x: 40, y: 44 },
    { id: 'lm',  label: 'LM', x: 20, y: 44 },
    { id: 'st1', label: 'ST', x: 62, y: 16 },
    { id: 'st2', label: 'ST', x: 38, y: 16 },
  ],
  '4-2-3-1': [
    { id: 'gk',  label: 'GK', x: 50, y: 85 },
    { id: 'rb',  label: 'RB', x: 80, y: 68 },
    { id: 'cb1', label: 'CB', x: 62, y: 68 },
    { id: 'cb2', label: 'CB', x: 38, y: 68 },
    { id: 'lb',  label: 'LB', x: 20, y: 68 },
    { id: 'dm1', label: 'DM', x: 62, y: 52 },
    { id: 'dm2', label: 'DM', x: 38, y: 52 },
    { id: 'ram', label: 'AM', x: 72, y: 34 },
    { id: 'cam', label: 'AM', x: 50, y: 32 },
    { id: 'lam', label: 'AM', x: 28, y: 34 },
    { id: 'st',  label: 'ST', x: 50, y: 14 },
  ],
  '3-5-2': [
    { id: 'gk',  label: 'GK', x: 50, y: 85 },
    { id: 'cb1', label: 'CB', x: 70, y: 68 },
    { id: 'cb2', label: 'CB', x: 50, y: 68 },
    { id: 'cb3', label: 'CB', x: 30, y: 68 },
    { id: 'rwb', label: 'RWB', x: 85, y: 48 },
    { id: 'cm1', label: 'CM', x: 66, y: 46 },
    { id: 'cm2', label: 'CM', x: 50, y: 44 },
    { id: 'cm3', label: 'CM', x: 34, y: 46 },
    { id: 'lwb', label: 'LWB', x: 15, y: 48 },
    { id: 'st1', label: 'ST', x: 62, y: 16 },
    { id: 'st2', label: 'ST', x: 38, y: 16 },
  ],
  '5-3-2': [
    { id: 'gk',  label: 'GK', x: 50, y: 85 },
    { id: 'rb',  label: 'RB', x: 85, y: 65 },
    { id: 'cb1', label: 'CB', x: 68, y: 65 },
    { id: 'cb2', label: 'CB', x: 50, y: 65 },
    { id: 'cb3', label: 'CB', x: 32, y: 65 },
    { id: 'lb',  label: 'LB', x: 15, y: 65 },
    { id: 'cm1', label: 'CM', x: 66, y: 44 },
    { id: 'cm2', label: 'CM', x: 50, y: 44 },
    { id: 'cm3', label: 'CM', x: 34, y: 44 },
    { id: 'st1', label: 'ST', x: 62, y: 16 },
    { id: 'st2', label: 'ST', x: 38, y: 16 },
  ],
  '4-1-4-1': [
    { id: 'gk',  label: 'GK', x: 50, y: 85 },
    { id: 'rb',  label: 'RB', x: 80, y: 68 },
    { id: 'cb1', label: 'CB', x: 62, y: 68 },
    { id: 'cb2', label: 'CB', x: 38, y: 68 },
    { id: 'lb',  label: 'LB', x: 20, y: 68 },
    { id: 'dm',  label: 'DM', x: 50, y: 54 },
    { id: 'rm',  label: 'RM', x: 80, y: 38 },
    { id: 'cm1', label: 'CM', x: 60, y: 38 },
    { id: 'cm2', label: 'CM', x: 40, y: 38 },
    { id: 'lm',  label: 'LM', x: 20, y: 38 },
    { id: 'st',  label: 'ST', x: 50, y: 14 },
  ],
};

const FORMATION_KEYS = Object.keys(FORMATIONS);

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

function firstNameOf(fullName = '') {
  return fullName.split(' ')[0] || fullName;
}

/** Color token by position label */
function tokenColor(label = '') {
  const l = label.toUpperCase();
  if (l === 'GK') return { bg: 'bg-amber-400', border: 'border-amber-500', text: 'text-amber-900' };
  if (['CB', 'CB1', 'CB2', 'CB3', 'RB', 'LB', 'RWB', 'LWB'].includes(l))
    return { bg: 'bg-blue-500', border: 'border-blue-600', text: 'text-white' };
  if (['CM', 'CM1', 'CM2', 'CM3', 'DM', 'DM1', 'DM2', 'RM', 'LM', 'AM', 'RAM', 'LAM', 'CAM'].includes(l))
    return { bg: 'bg-emerald-500', border: 'border-emerald-600', text: 'text-white' };
  // Forwards / Strikers / Wingers
  return { bg: 'bg-red-500', border: 'border-red-600', text: 'text-white' };
}

/* ─────────────────────────────────────────────
   Soccer Field Component
───────────────────────────────────────────── */
function SoccerField({ positions, assignments, players, onSlotTap }) {
  // Build a map: registrationId → player
  const playerMap = useMemo(() => {
    const m = {};
    players.forEach(p => { m[p.id] = p; });
    return m;
  }, [players]);

  return (
    <div
      className="relative w-full rounded-lg overflow-hidden select-none"
      style={{ backgroundColor: '#2d8a4e', paddingBottom: '154%' }} // ~65:100 aspect ratio
    >
      {/* ── Field markings ── */}

      {/* Outer border */}
      <div className="absolute inset-[3%] border-2 border-white/70 rounded-sm pointer-events-none" />

      {/* Center line */}
      <div className="absolute left-[3%] right-[3%] top-[50%] h-px bg-white/60 pointer-events-none" />

      {/* Center circle */}
      <div
        className="absolute border-2 border-white/60 rounded-full pointer-events-none"
        style={{ width: '28%', paddingBottom: '28%', left: '36%', top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Top penalty box (opponent's end) */}
      <div
        className="absolute border-2 border-white/60 pointer-events-none"
        style={{ left: '20%', right: '20%', top: '3%', height: '18%' }}
      />

      {/* Top goal box */}
      <div
        className="absolute border-2 border-white/60 pointer-events-none"
        style={{ left: '33%', right: '33%', top: '3%', height: '8%' }}
      />

      {/* Bottom penalty box (our end) */}
      <div
        className="absolute border-2 border-white/60 pointer-events-none"
        style={{ left: '20%', right: '20%', bottom: '3%', height: '18%' }}
      />

      {/* Bottom goal box */}
      <div
        className="absolute border-2 border-white/60 pointer-events-none"
        style={{ left: '33%', right: '33%', bottom: '3%', height: '8%' }}
      />

      {/* ── Position slots ── */}
      {positions.map(pos => {
        const registrationId = assignments[pos.id];
        const player = registrationId ? playerMap[registrationId] : null;
        const colors = tokenColor(pos.label);

        return (
          <button
            key={pos.id}
            type="button"
            onClick={() => onSlotTap(pos.id)}
            className="absolute flex flex-col items-center justify-center focus:outline-none group"
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: 'translate(-50%, -50%)',
              width: '13%',
              zIndex: 10,
            }}
            title={player ? player.playerName : pos.label}
          >
            {player ? (
              /* Filled slot */
              <div
                className={clsx(
                  'flex items-center justify-center rounded-full border-2 shadow-lg transition-transform group-hover:scale-105',
                  colors.bg, colors.border,
                )}
                style={{ width: '44px', height: '44px' }}
              >
                <span className={clsx('text-[10px] font-bold leading-tight text-center px-0.5', colors.text)}>
                  {firstNameOf(player.playerName).substring(0, 6)}
                </span>
              </div>
            ) : (
              /* Empty slot */
              <div
                className="flex flex-col items-center justify-center rounded-full border-2 border-dashed border-white/70 transition-all group-hover:border-white group-hover:bg-white/10"
                style={{ width: '44px', height: '44px' }}
              >
                <span className="text-white/80 text-[9px] font-semibold leading-none">{pos.label}</span>
                <PlusIcon className="h-3 w-3 text-white/60 mt-0.5" />
              </div>
            )}

            {/* Label below token */}
            <span className="mt-0.5 text-[9px] font-semibold text-white/90 leading-none drop-shadow">
              {player ? pos.label : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Player Picker Panel (bottom sheet style)
───────────────────────────────────────────── */
function PlayerPickerPanel({ position, players, assignments, onPick, onRemove, onClose }) {
  const assignedIds = new Set(Object.values(assignments).filter(Boolean));
  const currentPlayerId = assignments[position?.id];
  const currentPlayer = players.find(p => p.id === currentPlayerId);

  const available = players.filter(p => !assignedIds.has(p.id));

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50">
        <div>
          <p className="text-sm font-semibold text-zinc-800">
            {position?.label} — {currentPlayer ? 'Change or Remove' : 'Assign Player'}
          </p>
          {currentPlayer && (
            <p className="text-xs text-zinc-500">Currently: {currentPlayer.playerName}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Remove option */}
      {currentPlayer && (
        <button
          type="button"
          onClick={() => onRemove(position.id)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 transition-colors border-b border-zinc-100"
        >
          <XMarkIcon className="h-4 w-4" />
          Remove {currentPlayer.playerName}
        </button>
      )}

      {/* Available players list */}
      <div className="max-h-56 overflow-y-auto">
        {available.length === 0 ? (
          <p className="px-4 py-4 text-sm text-zinc-400 text-center">
            All players are assigned. Remove someone from the field first.
          </p>
        ) : (
          available.map(player => (
            <button
              key={player.id}
              type="button"
              onClick={() => onPick(position.id, player.id)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50 transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-blue-700">
                  {firstNameOf(player.playerName).charAt(0)}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-zinc-800 truncate">{player.playerName}</p>
                {player.parentName && (
                  <p className="text-xs text-zinc-400 truncate">{player.parentName}</p>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Position Picker (for bench → field)
───────────────────────────────────────────── */
function PositionPickerPanel({ player, positions, assignments, onAssign, onClose }) {
  const filledPositions = new Set(Object.keys(assignments).filter(k => assignments[k]));
  const emptyPositions = positions.filter(p => !filledPositions.has(p.id));

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50">
        <p className="text-sm font-semibold text-zinc-800">
          Assign {player?.playerName} to a position
        </p>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="max-h-56 overflow-y-auto">
        {emptyPositions.length === 0 ? (
          <p className="px-4 py-4 text-sm text-zinc-400 text-center">
            All positions are filled. Remove a player from the field first.
          </p>
        ) : (
          emptyPositions.map(pos => {
            const colors = tokenColor(pos.label);
            return (
              <button
                key={pos.id}
                type="button"
                onClick={() => onAssign(pos.id, player.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50 transition-colors"
              >
                <div className={clsx('h-8 w-8 rounded-full flex items-center justify-center shrink-0', colors.bg)}>
                  <span className={clsx('text-xs font-bold', colors.text)}>{pos.label.substring(0, 3)}</span>
                </div>
                <p className="text-sm font-medium text-zinc-800">{pos.label}</p>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
export default function LineupBuilder() {
  const { seasonId, sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession]             = useState(null);
  const [players, setPlayers]             = useState([]);
  const [formation, setFormation]         = useState('4-3-3');
  const [assignments, setAssignments]     = useState({});   // positionId → registrationId
  const [notes, setNotes]                 = useState('');
  const [saving, setSaving]               = useState(false);
  const [loading, setLoading]             = useState(true);

  // Selection state
  const [selectedPosition, setSelectedPosition]   = useState(null); // positionId
  const [benchPlayerPicking, setBenchPlayerPicking] = useState(null); // player object

  // Multi-coach race protection. The lineup is stored as a single doc
  // at lineups/{sessionId} with assignments: { posId → regId }. Naive
  // setDoc(..., { merge: true }) only merges the top level — the
  // assignments map is REPLACED wholesale, so two coaches editing
  // different positions clobber each other (Coach A writes { gk: X };
  // Coach B writes { lb: Z } a moment later → result is { lb: Z },
  // gk gone). Fix: track which positions/notes/formation the local
  // coach has actively edited and write them via Firestore field-path
  // updates (assignments.gk = X) so different positions commute.
  // The lineup onSnapshot listener also preserves dirty entries from
  // the previous local state to stop concurrent saves from wiping
  // the local coach's mid-edit state.
  const [dirtyPositions, setDirtyPositions] = useState(() => new Set());
  const [dirtyNotes, setDirtyNotes]         = useState(false);
  const [dirtyFormation, setDirtyFormation] = useState(false);
  const dirtyPositionsRef = useRef(dirtyPositions);
  const dirtyNotesRef     = useRef(dirtyNotes);
  const dirtyFormationRef = useRef(dirtyFormation);
  useEffect(() => { dirtyPositionsRef.current = dirtyPositions; }, [dirtyPositions]);
  useEffect(() => { dirtyNotesRef.current     = dirtyNotes; },     [dirtyNotes]);
  useEffect(() => { dirtyFormationRef.current = dirtyFormation; }, [dirtyFormation]);

  /* ── Load session ── */
  useEffect(() => {
    getDoc(doc(db, 'sessions', sessionId)).then(snap => {
      if (snap.exists()) setSession({ id: snap.id, ...snap.data() });
    });
  }, [sessionId]);

  /* ── Load confirmed registrations ── */
  // session.teamId is required by Firestore security rules
  useEffect(() => {
    if (!session?.teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('teamId',  '==', session.teamId),
      where('seasonId', '==', seasonId),
      where('status', 'in', ['confirmed', 'paid']),
      orderBy('playerName', 'asc'),
    );
    return onSnapshot(q, snap => {
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [seasonId, session?.teamId]);

  /* ── Load lineup doc (real-time) ── */
  useEffect(() => {
    const ref = doc(db, 'lineups', sessionId);
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        const serverAssignments = data.assignments || {};

        // Preserve dirty state from the previous local view.
        // Without this, a concurrent save by another coach would
        // wholesale replace formation/assignments/notes with the
        // server values — silently wiping the local coach's
        // in-progress edits.
        if (!dirtyFormationRef.current) {
          setFormation(data.formation || '4-3-3');
        }
        if (!dirtyNotesRef.current) {
          setNotes(data.notes || '');
        }
        setAssignments(prev => {
          const dirty = dirtyPositionsRef.current;
          if (dirty.size === 0) return serverAssignments;
          const next = { ...serverAssignments };
          for (const posId of dirty) {
            // Honor the local edit — including local removals (where
            // prev[posId] is undefined). The save() call will use
            // deleteField() for empty positions.
            if (posId in prev) {
              next[posId] = prev[posId];
            } else {
              delete next[posId];
            }
          }
          return next;
        });
      }
      setLoading(false);
    });
  }, [sessionId]);

  const positions = FORMATIONS[formation] || FORMATIONS['4-3-3'];

  /* ── Bench players: confirmed players NOT on the field ── */
  const assignedIds = useMemo(() => new Set(Object.values(assignments).filter(Boolean)), [assignments]);
  const benchPlayers = useMemo(() => players.filter(p => !assignedIds.has(p.id)), [players, assignedIds]);

  /* ── Formation change: clear assignments ── */
  //
  // Switching formations is destructive — old position IDs (e.g. 'rw'
  // in 4-3-3) don't exist in the new formation (4-4-2 has 'rm' instead),
  // so all assignments are cleared. If we already have assignments,
  // ask first so a misclick doesn't nuke a built lineup. Mark every
  // position from BOTH formations dirty so the save() call clears
  // stale keys from the doc via deleteField().
  const handleFormationChange = useCallback((newFormation) => {
    const hasAssignments = Object.values(assignments).some(Boolean);
    if (hasAssignments) {
      const ok = window.confirm(
        `Switching to ${newFormation} will clear your current lineup. Continue?`
      );
      if (!ok) return;
    }
    const oldKeys = (FORMATIONS[formation] || []).map(p => p.id);
    const newKeys = (FORMATIONS[newFormation] || []).map(p => p.id);
    setFormation(newFormation);
    setAssignments({});
    setSelectedPosition(null);
    setBenchPlayerPicking(null);
    setDirtyFormation(true);
    setDirtyPositions(new Set([...oldKeys, ...newKeys]));
  }, [assignments, formation]);

  /* ── Slot tap handler ── */
  const handleSlotTap = useCallback((positionId) => {
    setBenchPlayerPicking(null);
    setSelectedPosition(prev => prev === positionId ? null : positionId);
  }, []);

  /* ── Pick a player for a position ── */
  const handlePick = useCallback((positionId, registrationId) => {
    setAssignments(prev => ({ ...prev, [positionId]: registrationId }));
    setDirtyPositions(s => {
      const next = new Set(s);
      next.add(positionId);
      return next;
    });
    setSelectedPosition(null);
    setBenchPlayerPicking(null);
  }, []);

  /* ── Remove player from position ── */
  const handleRemove = useCallback((positionId) => {
    setAssignments(prev => {
      const next = { ...prev };
      delete next[positionId];
      return next;
    });
    setDirtyPositions(s => {
      const next = new Set(s);
      next.add(positionId);
      return next;
    });
    setSelectedPosition(null);
  }, []);

  /* ── Bench player tap: open position picker ── */
  const handleBenchPlayerTap = useCallback((player) => {
    setSelectedPosition(null);
    setBenchPlayerPicking(prev => prev?.id === player.id ? null : player);
  }, []);

  /* ── Assign bench player to position ── */
  const handleBenchAssign = useCallback((positionId, registrationId) => {
    setAssignments(prev => ({ ...prev, [positionId]: registrationId }));
    setDirtyPositions(s => {
      const next = new Set(s);
      next.add(positionId);
      return next;
    });
    setBenchPlayerPicking(null);
  }, []);

  /* ── Save lineup ── */
  //
  // Uses Firestore field-path writes (`assignments.gk = X`) so two
  // coaches editing different positions never clobber each other's
  // assignments map. Only fields the local coach has actively touched
  // (tracked via dirty* flags/sets) get written — fields untouched
  // locally take no part in the write at all, so the other coach's
  // edits to those fields stay intact.
  const save = async () => {
    const dirtyPos = dirtyPositionsRef.current;
    const dirtyN   = dirtyNotesRef.current;
    const dirtyF   = dirtyFormationRef.current;
    if (dirtyPos.size === 0 && !dirtyN && !dirtyF) return; // nothing to do

    setSaving(true);
    try {
      const ref = doc(db, 'lineups', sessionId);

      const update = {
        // Always-stamped fields (cheap, no clobber risk — they're
        // identifiers, not user-edited state).
        sessionId,
        seasonId,
        teamId:    session?.teamId || null,
        updatedAt: serverTimestamp(),
      };

      if (dirtyF) update.formation = formation;
      if (dirtyN) update.notes     = notes;

      // Per-position field-path writes. An empty local value (player
      // removed) becomes deleteField() so the doc is properly cleared
      // — without this, removing a player from a slot wouldn't
      // actually erase it from the doc.
      for (const posId of dirtyPos) {
        const regId = assignments[posId];
        update[`assignments.${posId}`] = regId || deleteField();
      }

      await setDoc(ref, update, { merge: true });

      // Clear dirty flags only after the commit succeeds. If the
      // write throws, the user's edits stay marked dirty so they
      // can retry without losing state.
      setDirtyPositions(new Set());
      setDirtyNotes(false);
      setDirtyFormation(false);
      toast.success('Lineup saved!');
    } catch (err) {
      console.error('[LineupBuilder] save error', err);
      toast.error('Failed to save lineup.');
    } finally {
      setSaving(false);
    }
  };

  const sessionDate = toDate(session?.date);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-20">
      {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
      <TeamSubNav teamId={session?.teamId} />

      {/* ── Header ── */}
      <div className="mb-5">
        <button
          onClick={() => navigate(`/seasons/${seasonId}/attendance/${sessionId}`)}
          className="mb-3 flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to Session
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Heading level={1}>Lineup Builder</Heading>
            <Text>
              {session?.type ?? 'Session'}
              {sessionDate ? ` · ${format(sessionDate, 'EEEE, MMM d, yyyy')}` : ''}
            </Text>
          </div>
          <Button color="blue" onClick={save} disabled={saving}>
            {saving
              ? <><ArrowPathIcon className="h-4 w-4 animate-spin mr-1.5" />Saving…</>
              : 'Save Lineup'
            }
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(n => <div key={n} className="h-24 rounded-xl bg-zinc-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="max-w-sm mx-auto space-y-4">

          {/* ── Formation picker ── */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-600 shrink-0">Formation:</span>
            <select
              value={formation}
              onChange={e => handleFormationChange(e.target.value)}
              className="flex-1 min-w-0 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-800 shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
            >
              {FORMATION_KEYS.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          {/* ── Soccer field ── */}
          <SoccerField
            positions={positions}
            assignments={assignments}
            players={players}
            onSlotTap={handleSlotTap}
          />

          {/* ── Position picker panel ── */}
          {selectedPosition && (
            <PlayerPickerPanel
              position={positions.find(p => p.id === selectedPosition)}
              players={players}
              assignments={assignments}
              onPick={handlePick}
              onRemove={handleRemove}
              onClose={() => setSelectedPosition(null)}
            />
          )}

          {/* ── Position picker for bench player ── */}
          {benchPlayerPicking && (
            <PositionPickerPanel
              player={benchPlayerPicking}
              positions={positions}
              assignments={assignments}
              onAssign={handleBenchAssign}
              onClose={() => setBenchPlayerPicking(null)}
            />
          )}

          {/* ── Bench / Subs section ── */}
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-100 bg-zinc-50">
              <UserGroupIcon className="h-4 w-4 text-zinc-500" />
              <span className="text-sm font-semibold text-zinc-700">
                Bench / Subs
                <span className="ml-1.5 text-xs font-normal text-zinc-400">
                  ({benchPlayers.length} of {players.length})
                </span>
              </span>
            </div>

            {players.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <UserGroupIcon className="mx-auto h-8 w-8 text-zinc-300 mb-2" />
                <p className="text-sm text-zinc-400">No confirmed players yet.</p>
                <p className="text-xs text-zinc-300 mt-0.5">Players appear once their registration is confirmed.</p>
              </div>
            ) : benchPlayers.length === 0 ? (
              <p className="px-4 py-4 text-sm text-zinc-400 text-center">All players are on the field!</p>
            ) : (
              <div className="divide-y divide-zinc-50">
                {benchPlayers.map(player => {
                  const isPickingThis = benchPlayerPicking?.id === player.id;
                  return (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => handleBenchPlayerTap(player)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                        isPickingThis ? 'bg-blue-50' : 'hover:bg-zinc-50',
                      )}
                    >
                      <div className={clsx(
                        'h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors',
                        isPickingThis ? 'bg-blue-500' : 'bg-zinc-100',
                      )}>
                        <span className={clsx(
                          'text-xs font-bold',
                          isPickingThis ? 'text-white' : 'text-zinc-600',
                        )}>
                          {firstNameOf(player.playerName).charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-zinc-800 truncate">{player.playerName}</p>
                        {player.parentName && (
                          <p className="text-xs text-zinc-400 truncate">{player.parentName}</p>
                        )}
                      </div>
                      <PlusIcon className={clsx(
                        'h-4 w-4 shrink-0 transition-colors',
                        isPickingThis ? 'text-blue-500' : 'text-zinc-300',
                      )} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Coach notes ── */}
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
              <p className="text-sm font-semibold text-zinc-700">Coach Notes</p>
              <p className="text-xs text-zinc-400">Game day tactics, instructions, etc.</p>
            </div>
            <div className="p-4">
              <textarea
                value={notes}
                onChange={e => {
                  setNotes(e.target.value);
                  // Mark notes dirty so the lineup onSnapshot listener
                  // won't overwrite our draft if another coach saves
                  // a different field concurrently, and so save()
                  // knows to write notes (skipping the field would
                  // leave the user wondering where their changes went).
                  setDirtyNotes(true);
                }}
                placeholder="e.g. Press high, avoid long balls…"
                rows={3}
                className="w-full resize-none rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-800 placeholder-zinc-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>

          {/* ── Save button (bottom) ── */}
          <Button color="blue" fullWidth onClick={save} disabled={saving}>
            {saving
              ? <><ArrowPathIcon className="h-4 w-4 animate-spin mr-1.5" />Saving…</>
              : 'Save Lineup'
            }
          </Button>
        </div>
      )}
    </div>
  );
}
